import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Send, Loader2, CheckCircle2 } from "lucide-react";

const EMPTY = { name: "", email: "", subject: "", message: "" };

export default function ContactPage() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => window.scrollTo(0, 0), []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm(EMPTY);
      toast.success("Message envoyé, merci !");
    } catch (err) {
      toast.error(err.response?.status === 422 ? "Vérifiez les champs (email valide, message de 5 caractères min.)" : "Envoi impossible, réessayez.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page" className="max-w-5xl mx-auto px-4 sm:px-8 py-16 grid md:grid-cols-[1fr_1.3fr] gap-12">
      <div>
        <p className="text-xs font-semibold tracking-widest uppercase text-emerald-700/80 flex items-center gap-2">
          <Mail className="w-4 h-4" /> Contact
        </p>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-emerald-950 mt-2">Une question, un spot à proposer ?</h1>
        <p className="text-slate-600 mt-4 leading-relaxed">
          Écrivez-nous via ce formulaire, nous vous répondrons rapidement. Vous pouvez aussi nous écrire directement à{" "}
          <a href="mailto:bhabily@hotmail.com" data-testid="contact-mailto" className="text-emerald-700 font-semibold underline">bhabily@hotmail.com</a>.
        </p>
      </div>

      {sent ? (
        <div data-testid="contact-success" className="rounded-3xl bg-emerald-50 border border-emerald-200 p-10 text-center self-start">
          <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto" />
          <p className="font-display text-xl font-bold text-emerald-950 mt-3">Message bien reçu !</p>
          <p className="text-sm text-slate-600 mt-1">Merci, nous revenons vers vous très vite.</p>
          <button onClick={() => setSent(false)} data-testid="contact-new-message" className="mt-5 text-sm font-semibold text-emerald-700 underline">
            Envoyer un autre message
          </button>
        </div>
      ) : (
        <form onSubmit={submit} className="rounded-3xl bg-white border border-emerald-900/10 p-6 sm:p-8 space-y-4" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Input required placeholder="Votre nom" value={form.name} onChange={set("name")} data-testid="contact-name" className="rounded-xl" />
            <Input required type="email" placeholder="Votre email" value={form.email} onChange={set("email")} data-testid="contact-email" className="rounded-xl" />
          </div>
          <Input required placeholder="Sujet" value={form.subject} onChange={set("subject")} data-testid="contact-subject" className="rounded-xl" />
          <Textarea required minLength={5} rows={6} placeholder="Votre message…" value={form.message} onChange={set("message")} data-testid="contact-message" className="rounded-xl" />
          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit"
            className="w-full flex items-center justify-center gap-2 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-60 text-white font-semibold py-3.5 rounded-full transition-colors"
          >
            {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />} Envoyer
          </button>
        </form>
      )}
    </div>
  );
}
